import * as React from "react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { useAuth } from "@/hooks/useAuth";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { ThemeProvider } from "@/hooks/useTheme";
import { appQueryClient, wipeClientCaches } from "@/lib/queryClient";
import { createQueryPersister } from "@/lib/queryPersist";
import { warmCriticalQueries } from "@/lib/warmQueries";
import { useStorageQuotaWarning } from "@/hooks/useStorageQuotaWarning";
import { PwaUpdateToast } from "@/components/pwa";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { OFFLINE_BANNER_MSG } from "@/lib/offline";

export { wipeClientCaches };

function AuthLoadingShell() {
  const online = useOnlineStatus();
  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 dark:bg-slate-950">
      {!online ? (
        <div
          role="status"
          className="border-b border-amber-300 bg-amber-100 px-4 py-2 text-center text-sm font-medium text-amber-950 dark:border-amber-800 dark:bg-amber-950/80 dark:text-amber-100"
          data-testid="offline-banner"
        >
          {OFFLINE_BANNER_MSG}
        </div>
      ) : null}
      <div className="flex flex-1 flex-col items-center justify-center gap-2">
        <LoadingSpinner />
        <p className="text-sm text-slate-500 dark:text-slate-400">Betöltés…</p>
      </div>
    </div>
  );
}

/**
 * Batch B — wait for auth before mounting the persister so we never hydrate/bust
 * under the "anon" key then remount as the real user (empty offline cache symptom).
 */
export function AppProviders({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const userId = user?.id ?? null;
  const prevUserId = React.useRef<string | null | undefined>(undefined);

  useStorageQuotaWarning();

  React.useEffect(() => {
    const prev = prevUserId.current;
    prevUserId.current = userId;
    if (prev === undefined) return;
    if (prev && prev !== userId) {
      void wipeClientCaches(prev);
    }
  }, [userId]);

  // Explicit reconnect warm: RQ refetchOnReconnect + optional critical prefetch
  React.useEffect(() => {
    const onOnline = () => {
      if (!userId) return;
      void appQueryClient.refetchQueries({ type: "active" });
      void warmCriticalQueries(appQueryClient);
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [userId]);

  const persister = React.useMemo(() => createQueryPersister(userId), [userId]);

  return (
    <ThemeProvider>
      {isLoading ? (
        <AuthLoadingShell />
      ) : (
        <PersistQueryClientProvider
          key={userId ?? "anon"}
          client={appQueryClient}
          persistOptions={{
            persister,
            maxAge: 1000 * 60 * 60 * 24,
            buster: userId ?? "anon",
            dehydrateOptions: {
              shouldDehydrateQuery: (q) => q.state.status === "success"
            }
          }}
          onSuccess={() => {
            if (userId && typeof navigator !== "undefined" && navigator.onLine) {
              void warmCriticalQueries(appQueryClient);
            }
          }}
        >
          {children}
          <PwaUpdateToast />
        </PersistQueryClientProvider>
      )}
    </ThemeProvider>
  );
}
